import { datetimeLocalToEpoch, epochToDatetimeLocal } from 'utils/dates/date.utils';
import FloodSettingsField from '../../../components/FloodSettingsField';
import {
  FIELD_HELP,
  SCENARIO_SUMMARIES,
  scenarioNullableFields,
  scenarioParamDefs,
} from '../config/floodSimConfig';
import type {
  CatalogParam,
  CatalogScenario,
  FloodSimSettingsProps,
  ScenarioName,
  SimSettings,
} from '../FloodSimSettings.types';
import WaterwaySection from './WaterwaySection';
import SettingsSection from './SettingsSection';

interface ScenarioModeSectionProps {
  sim: SimSettings | null | undefined;
  set: <K extends keyof SimSettings>(field: K, value: SimSettings[K]) => void;
  setScenarioParam: (field: string, value: number) => void;
  onChange: (sim: SimSettings) => void;
  /** The simulator's catalogue. Undefined while loading or when the read
   *  failed — the section then falls back to the local eight. */
  catalog?: CatalogScenario[];
  /** This device — its id and pin, for the waterway link. */
  device?: FloodSimSettingsProps['device'];
  hasUnsavedChanges?: boolean;
  onOpenDevice?: FloodSimSettingsProps['onOpenDevice'];
  pendingDownstream?: FloodSimSettingsProps['pendingDownstream'];
  onStageDownstream?: FloodSimSettingsProps['onStageDownstream'];
  onUnstageDownstream?: FloodSimSettingsProps['onUnstageDownstream'];
}

/** The picker's rows: the catalogue when it answered, the local fallback otherwise. */
function pickerRows(catalog: CatalogScenario[] | undefined): CatalogScenario[] {
  if (catalog && catalog.length > 0) return catalog;
  return Object.entries(scenarioParamDefs).map(([name, defs]) => ({
    name,
    family: 'threshold',
    summary: SCENARIO_SUMMARIES[name] ?? '',
    params: defs.map<CatalogParam>((d) => ({
      name: d.key,
      type: d.type === 'int' ? 'integer' : 'number',
      default: d.default,
      derivesFromThresholds: false,
    })),
  }));
}

/** "ticksBeforeCross" → "Ticks Before Cross", for a label. */
function labelOf(param: CatalogParam): string {
  return param.name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ScenarioModeSection({
  sim,
  set,
  setScenarioParam,
  onChange,
  catalog,
  device,
  hasUnsavedChanges,
  onOpenDevice,
  pendingDownstream,
  onStageDownstream,
  onUnstageDownstream,
}: ScenarioModeSectionProps) {
  const rows = pickerRows(catalog);

  const currentName: ScenarioName = sim?.scenario_name ?? rows[0]?.name ?? 'branch_sweep';
  const current = rows.find((r) => r.name === currentName);
  const families = Array.from(new Set(rows.map((r) => r.family)));

  return (
    <>
      <SettingsSection
        title="Scenario model"
        description="Choose a model and tune the parameters used to generate demo gauge readings."
      >
        {/* Scenario picker — resets scenario_params to the scenario's defaults on change */}
        <FloodSettingsField
          variant="select"
          label="Scenario"
          help={FIELD_HELP.scenario_name}
          value={currentName}
          onChange={(value) => {
            const name = value as ScenarioName;
            const chosen = rows.find((r) => r.name === name);
            const defaults: Record<string, number> = {};
            (chosen?.params ?? []).forEach((p) => (defaults[p.name] = p.default));
            onChange({
              ...(sim as SimSettings),
              scenario_name: name,
              scenario_params: defaults,
            });
          }}
          options={families.map((family) => ({
            label:
              family === 'hydrology'
                ? 'Hydrology — real gauge patterns'
                : 'Threshold — branch testing',
            options: rows
              .filter((row) => row.family === family)
              .map((row) => ({
                value: row.name,
                label: row.summary ? `${row.name} — ${row.summary}` : row.name,
              })),
          }))}
        />

        {/* What the chosen scenario does, in the words its author wrote. */}
        {current?.summary ? (
          <div className="text-theme-muted small">
            <span className="font-monospace">{current.name}</span>: {current.summary}
          </div>
        ) : null}

        {/* Dynamic param fields for the selected scenario */}
        {(current?.params ?? []).map((param) => (
          <FloodSettingsField
            key={param.name}
            label={labelOf(param)}
            help={
              param.description
                ? `${param.description}${param.derivesFromThresholds ? ' (0 = derived from this device\u2019s thresholds.)' : ''}`
                : param.derivesFromThresholds
                  ? 'A value of 0 means: derive it from this device\u2019s own thresholds.'
                  : undefined
            }
            type="number"
            step={param.type === 'integer' ? 1 : 0.1}
            min={param.min}
            max={param.max}
            placeholder={
              param.derivesFromThresholds ? '0 = from thresholds' : String(param.default)
            }
            value={sim?.scenario_params?.[param.name] ?? param.default}
            onChange={(e) =>
              setScenarioParam(
                param.name,
                param.type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value),
              )
            }
          />
        ))}

        {/* Nullable ID fields (global_action_id, base_donor_uid) */}
        {scenarioNullableFields.map(({ label, field, help }) => (
          <FloodSettingsField
            key={field}
            label={label}
            help={help}
            type="number"
            value={sim?.[field] ?? ''}
            onChange={(e) => set(field, e.target.value === '' ? null : parseInt(e.target.value))}
          />
        ))}
      </SettingsSection>

      {/* ── Waterway: the chain, the upstream link, the downstream links ── */}
      <WaterwaySection
        sim={sim}
        onChange={onChange}
        device={device}
        hasUnsavedChanges={hasUnsavedChanges}
        onOpenDevice={onOpenDevice}
        pendingDownstream={pendingDownstream}
        onStageDownstream={onStageDownstream}
        onUnstageDownstream={onUnstageDownstream}
      />

      {/* Base donor timestamp for scenario replay */}
      <FloodSettingsField
        label="Base Donor Timestamp"
        help={FIELD_HELP.base_donor_ts}
        type="datetime-local"
        value={epochToDatetimeLocal(sim?.base_donor_ts)}
        onChange={(e) => set('base_donor_ts', datetimeLocalToEpoch(e.target.value))}
      />
    </>
  );
}
